
import { computed, defineComponent, PropType, ref } from "vue";
import PopupConfirmButton from "@/components/buttons/PopupConfirmButton/PopupConfirmButton.vue";
import {
  StudentClass,
  studentClassToClass,
} from "@/ts/objects/value/student-class";
import StudentClassesSelectPopupItem from "@/components/popups/StudentClassesSelectPopup/StudentClassesSelectPopupItem/StudentClassesSelectPopupItem.vue";
import { SchoolType } from "@/ts/objects/value/school-type";
import { Class } from "@/ts/objects/entity/class";
import { useUserService } from "@/composables/provide-user-service";
import {
  filterNotNullish,
  hasDuplicates,
  hasValue,
} from "@/ts/utils/common-util";
import AddButton from "@/components/buttons/AddButton/AddButton.vue";
import { useAppStore } from "@/store/app-store";
import { createGradeOrError } from "@/ts/objects/value/grade";
import log from "loglevel";

export default defineComponent({
  name: "StudentClassesSelectPopup",
  components: { AddButton, StudentClassesSelectPopupItem, PopupConfirmButton },
  props: {
    initialValue: { type: Array as PropType<StudentClass[]>, required: true },
    text: { type: String, default: "クラスを設定してください。" },

    onConfirm: {
      type: Function as PropType<(classes: StudentClass[]) => void>,
      required: true,
    },
    onClickOutside: {
      type: Function as PropType<() => void>,
      required: true,
    },
  },
  setup(props) {
    const appStore = useAppStore();
    const userService = useUserService();

    const items = ref<Item[]>(
      props.initialValue.map((sc) => ({
        schoolYear: sc.schoolYear,
        schoolType: sc.grade.schoolType,
        gradeNumber: sc.grade.gradeNumber,
        cls: studentClassToClass(sc),
        studentNumber: sc.studentNumber,
      })),
    );

    const cachedClasses: Record<number, Class[]> = {};

    // ちょっとリクエスト投げすぎにも思える・・・そのうち改善すべきかも。
    const getClasses = async (schoolYear: number): Promise<Class[]> => {
      if (schoolYear < 1970 || 9999 < schoolYear) return [];

      const classes: Class[] | undefined = cachedClasses[schoolYear];
      if (hasValue(classes)) return classes;

      const result = await userService.listClasses(schoolYear);
      if (!result.ok) return []; // もっとちゃんとエラーハンドリングする？

      cachedClasses[schoolYear] = result.data;
      return result.data;
    };

    const width = 460;
    const itemWidth = 330;

    const itemSchoolYears = computed(() =>
      items.value.map((i) => i.schoolYear),
    );
    const sameSchoolYearExists = computed(() =>
      hasDuplicates(filterNotNullish(itemSchoolYears.value)),
    );
    const canConfirm = computed(() => {
      return (
        !sameSchoolYearExists.value &&
        items.value.every((i) => isItemComplete(i))
      );
    });

    const message = computed(() =>
      sameSchoolYearExists.value
        ? "同じ年度のクラスを複数指定することはできません。"
        : null,
    );

    return {
      items,

      getClasses,
      sameSchoolYearExists,
      canConfirm,

      message,

      itemWidth,

      onClickAddItem: () => {
        items.value.push({
          schoolYear: appStore.currentSchoolYear,
          schoolType: "elementary",
          gradeNumber: 1,
          cls: null,
          studentNumber: null,
        });
      },
      onChangeItem: (
        idx: number,
        schoolYear: number | null,
        schoolType: SchoolType,
        gradeNumber: number | null,
        cls: Class | null,
        studentNumber: number | null,
      ) => {
        items.value.splice(idx, 1, {
          schoolYear,
          schoolType,
          gradeNumber,
          cls,
          studentNumber,
        });
      },
      onClickRemoveItem: (idx: number) => {
        items.value.splice(idx, 1);
      },
      onConfirmInternal: () => {
        log.debug(`onConfirmInternal: items=${JSON.stringify(items)}`);
        const studentClasses: StudentClass[] = items.value
          .filter((i): i is CompleteItem => isItemComplete(i))
          .map((i) => ({
            classId: i.cls.classId,
            schoolYear: i.schoolYear,
            grade: createGradeOrError(i.schoolType, i.gradeNumber),
            classNo: i.cls.classNo,
            name: i.cls.name,
            studentNumber: i.studentNumber,
          }));
        log.debug(
          `onConfirmInternal: studentClasses=${JSON.stringify(studentClasses)}`,
        );
        props.onConfirm(studentClasses);
      },
      styles: {
        "--width": `${width}px`,
      },

      // テンプレート中で?を使うとStorybookがエラー吐くので・・・。
      toClassId: (cls: Class | null | undefined) => cls?.classId ?? null,
    };
  },
});

type Item = {
  readonly schoolYear: number | null;
  readonly schoolType: SchoolType;
  readonly gradeNumber: number | null;
  readonly cls: Class | null;
  readonly studentNumber: number | null;
};

type CompleteItem = {
  readonly schoolYear: number;
  readonly schoolType: SchoolType;
  readonly gradeNumber: number;
  readonly cls: Class;
  readonly studentNumber: number;
};

function isItemComplete(item: Item): item is CompleteItem {
  return (
    hasValue(item.schoolYear) &&
    hasValue(item.schoolType) &&
    hasValue(item.gradeNumber) &&
    hasValue(item.cls)
  );
}
